import React, { Component } from 'react';
import PropTypes from 'prop-types';

class VideoStream extends Component {
  constructor() {
    super();

    this.state = { 
      width: window.innerWidth, 
      height: window.innerHeight
    };

    this.stream = null;
    this.streamWidth = 0;
    this.streamHeight = 0;
    this.scaleFactor = 1;
    this.video = null;
    this.canvasContext = null;
    this.handleResize = this.handleResize.bind(this);
  }

  videoStyles = () => {
    var heightRatio = this.streamHeight / this.state.height;
    var widthRatio = this.streamWidth / this.state.width;
    var scaleFactor = 1 / Math.min(heightRatio, widthRatio);
    this.scaleFactor = Number.isFinite(scaleFactor)? scaleFactor : 1;

    return {
      position: 'relative',
      top: '50%',
      left: '50%',
      transformOrigin: '50% 50%',
      transform: `translate(-50%, -50%) scale(${this.scaleFactor})`
    };
  }

  handleResize = () => {
    this.setState({
      width: window.innerWidth,
      height: window.innerHeight 
    });
  }

  async componentDidMount() {
    window.addEventListener('resize', this.handleResize);

    let initSuccess = true;
    let message = '';
    try {
      await this.startCamera();
    } catch (e) {
      message = `Browser camera init error: ${e}`;
      initSuccess = false;
    }

    if (typeof this.props.onInit === 'function') {
      this.props.onInit({ error: initSuccess, message }, this.drawFrame);
    }
  }

  componentWillUnmount() {
    this.stopCamera();
  }

  stopCamera = () => {
    if (!this.stream) return;
    this.stream.getTracks().map(t => t.stop());
    this.stream = null;
    this.streamWidth = 0;
    this.streamHeight = 0;
    this.canvasContext = null;
  };

  startCamera = async () => {
    this.stopCamera();

    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      throw new Error('WebRTC API not supported in this browser');
    }

    let options = { audio: false, video: { facingMode: { exact: 'environment' } } };
    if(navigator.mediaDevices.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      devices.forEach((device) => {
        if (device.kind === 'videoinput' && device.label.toLowerCase().search("back") > -1) {
          options = {video: {'deviceId': {'exact':device.deviceId}, 'facingMode':'environment'}};
        }
      });
    }
    this.stream = await navigator.mediaDevices.getUserMedia(options);

    if (this.video.srcObject !== undefined) {
      this.video.srcObject = this.stream;
    } else if (this.video.mozSrcObject !== undefined) {
      this.video.mozSrcObject = this.stream;
    } else if (window.URL.createObjectURL) {
      this.video.src = window.URL.createObjectURL(this.stream);
    } else if (window.webkitURL) {
      this.video.src = window.webkitURL.createObjectURL(this.stream);
    } else {
      this.video.src = this.stream;
    }

    this.video.playsInline = true;
    this.video.play(); // firefox does not emit `loadeddata` if video not playing
    await this.streamLoadedPromise();

    this.streamWidth = this.video.videoWidth;
    this.streamHeight = this.video.videoHeight;
    this.handleResize();

    if (!this.canvasContext) {
      const canvas = document.createElement('canvas');
      canvas.width = this.streamWidth;
      canvas.height = this.streamHeight;
      this.canvasContext = canvas.getContext('2d');
    }
  };

  streamLoadedPromise = () => new Promise((resolve, reject) => {
    this.video.addEventListener('loadeddata', resolve, { once: true });
    this.video.addEventListener('error', reject, { once: true });
  });

  captureFrame = () => {
    this.canvasContext.drawImage(this.video, 0, 0, this.streamWidth, this.streamHeight);
    return this.canvasContext.getImageData(0, 0, this.streamWidth, this.streamHeight);
  }

  drawFrame = () => {
    window.requestAnimationFrame(() => {
      if (!this.canvasContext) return;
      const { data } = this.captureFrame();
      this.props.onFrame({
        data,
        width: this.streamWidth,
        height: this.streamHeight,
      });
    });
  };
  
  render() {
    return <video style={{...this.videoStyles(), ...this.props.style}} ref={v => (this.video = v)} />
  }
};

VideoStream.propTypes = {
  onInit: PropTypes.func.isRequired,
  onFrame: PropTypes.func.isRequired,
  style: PropTypes.object,
  rearCamera: PropTypes.bool,
};

VideoStream.defaultProps = {
  style: {},
  rearCamera: true,
};

export default VideoStream;


// WEBPACK FOOTER //
// ./src/components/ReactiveQR/video_stream.js